import {
  BarChart2,
  ChevronRight,
  HelpCircle,
  Home,
  LineChart,
  Settings,
  ShoppingBag,
  X,
  Zap,
  Package,
  Users,
  TrendingUp,
  SettingsIcon,
  CreditCardIcon,
  TrashIcon,
  BookOpen,
  DollarSignIcon,
  TicketIcon,
  GlobeIcon,
} from "lucide-react";
import { useContext, useState, useEffect } from "react";
import { Button } from "../ui/button";
import { UserStateContext } from "@/contexts/UserStateProvider";
import { useNavigate, useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import { MergoioLogo } from "./MergoioLogo";
import { SwitchSellerAccount } from "../switch-seller-account";
import { OrderCount } from "../order-count";
import { ModeToggle } from "../mode-toggle";
import { Badge } from "../ui/badge";

export const _Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSidebarOpen, setIsSidebarOpen } = useContext(UserStateContext);
  const [activeMenuItem, setActiveMenuItem] = useState("");
  const [openSubmenus, setOpenSubmenus] = useState<string[]>([]);

  const toggleSubmenu = (item: string) => {
    setOpenSubmenus((prev) =>
      prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]
    );
  };
  const menuItems = [
    // {
    //   icon: BookOpen,
    //   text: "Summary", 
    //   color: "from-orange-400 to-orange-600",
    //   link: "/",
    // },
    {
      icon: LineChart,
      text: "Sales & Profit",
      color: "from-blue-400 to-blue-600",
      link: "/",
    },
    {
      icon: ShoppingBag,
      text: "Your Shop",
      color: "from-green-400 to-green-600",
      subItems: [
        {
          icon: Package,
          text: "Products",
          link: "/products",
          description: "Manage your product catalog",
        },
        // { icon: ShoppingCart, text: "Orders", link: "/orders", description: "View and manage orders" },
        // { icon: Users, text: "Customers", link: "/customers", description: "Manage customer information" },
        // { icon: Users, text: "Affiliates", link: "/affiliates", description: "Manage affiliate programs" }
      ],
    },
    {
      icon: BarChart2,
      text: "Analytics",
      color: "from-purple-400 to-purple-600",
      subItems: [
        {
          icon: Package,
          text: "Products",
          link: "/analytics/products",
          description: "View performance metrics by product",
        },
        {
          icon: TrashIcon,
          text: "Cancellations",
          link: "/analytics/cancellations",
          description: "View cancellation metrics by product",
        },
        // { icon: TrendingUp, text: "Sales & Profit", link: "/analytics/sales-profit", description: "Analyze sales and profit data" },
        {
          icon: Users,
          text: "Affiliates",
          link: "/analytics/affiliates",
          description: "View affiliate performance over time",
        },
        {
          icon: DollarSignIcon,
          text: "Settlements",
          link: "/analytics/settlements",
          description: "View settlement figures by settlement time",
          beta: true
        },
        {
          icon: TicketIcon,
          text: "Samples",
          link: "/analytics/samples",
          description: "View analytics on sample orders",
          beta: true
        },
        // {
        //   icon: ShoppingBag,
        //   text: "Orders",
        //   link: "/analytics/orders",
        //   description: "View performance metrics per order",
        //   beta: true
        // },
      ],
    },
    {
      icon: TicketIcon,
      text: "Expense Items",
      color: "from-orange-400 to-orange-600",
      link: "/expenseItems",
      description: "Manage expense items"
    },
    // {
    //   icon: Zap,
    //   text: "AI Insights",
    //   color: "from-pink-400 to-pink-600",
    //   subItems: [
    //     { icon: TrendingUp, text: "Trend Analysis", link: "/ai-insights/trend-analysis", description: "AI-powered trend predictions" },
    //     { icon: BarChart2, text: "Performance Predictions", link: "/ai-insights/performance-predictions", description: "AI-generated performance forecasts" },
    //     { icon: MessageSquare, text: "Content Suggestions", link: "/ai-insights/content-suggestions", description: "AI-powered content recommendations" },
    //   ],
    // },
    // { icon: Settings, text: "Settings", color: "from-red-400 to-red-600", link: "/settings" },
    {
      icon: GlobeIcon,
      text: "Organisation",
      color: "from-red-400 to-red-600",
      subItems: [
        {
          icon: SettingsIcon,
          text: "Settings",
          link: "/settings",
          description: "Configure organisation settings"
        },
        {
          icon: CreditCardIcon, 
          text: "Billing",
          link: "/billing",
          description: "Manage billing and subscriptions"
        },
        {
          icon: TrendingUp,
          text: "Ad Accounts",
          link: "/tiktok/businessAccounts", 
          description: "Manage TikTok ad accounts"
        }
      ]
    },
    
    {
      icon: HelpCircle,
      text: "Seller Forum",
      color: "from-teal-400 to-teal-600",
      externalLink: "https://forum.mergoio.com",
    },
    {
      icon: HelpCircle,
      text: "Help",
      color: "from-gray-400 to-gray-600",
      link: "/help",
    },
  ];

  useEffect(() => {
    const currentPath = location.pathname;
    const activeItem = menuItems.find(
      (item) =>
        (item.link &&
          (currentPath === item.link ||
            (item.link === "/" && currentPath === "/home"))) ||
        (item.subItems &&
          item.subItems.some(
            (subItem) => subItem.link && currentPath.startsWith(subItem.link)
          ))
    );
    if (activeItem) {
      setActiveMenuItem(activeItem.text);
      if (activeItem.subItems) {
        setOpenSubmenus((prev) =>
          prev.includes(activeItem.text) ? prev : [...prev, activeItem.text]
        );
      }
    }
  }, [location, menuItems]);

  return (
    <aside
      className={`light:bg-gray-400 dark:bg-black border-r w-64 min-h-screen max-h-screen flex flex-col transition-all duration-300 ease-in-out ${
        isSidebarOpen ? "translate-x-0" : "-translate-x-full"
      } md:translate-x-0 md:static fixed z-30 shadow-sm`}
    >
      <div className="flex items-center justify-between h-16 px-4 pt-4 border-b border-border/40 pb-4">
        <MergoioLogo />
        <div className="flex items-center gap-2">
          <ModeToggle />
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="md:hidden text-foreground hover:bg-muted rounded-full p-1 transition-colors"
          >
            <X size={18} />
          </button>
        </div>
      </div>
      
      <nav className="flex-grow py-4 overflow-y-auto scrollbar-thin">
        <ul className="space-y-0.5 px-3">
          {menuItems.map((item, index) => (
            <li key={index} className="mb-1">
              <Button
                variant={activeMenuItem === item.text ? "secondary" : "ghost"}
                className={`group w-full justify-start px-3 py-2 text-sm font-medium hover:bg-muted/70 transition-all duration-200 rounded-md ${
                  activeMenuItem === item.text ? "text-primary font-semibold" : "text-muted-foreground"
                }`}
                onClick={() => {
                  if (item.externalLink) {
                    window.open(item.externalLink, '_blank');
                    return;
                  }
                  if (item.subItems) {
                    toggleSubmenu(item.text);
                  } else if (item.link) {
                    setActiveMenuItem(item.text);
                    navigate(item.link);
                  }
                }}
              >
                <div
                  className={`w-7 h-7 mr-3 rounded-md bg-gradient-to-br ${item.color} flex items-center justify-center transition-transform duration-300 ease-in-out group-hover:scale-110`}
                >
                  <item.icon className="w-4 h-4 text-white" />
                </div>
                <span className="truncate">{item.text}</span>
                {item.subItems && (
                  <ChevronRight
                    size={14}
                    className={`ml-auto transition-transform duration-200 ${
                      openSubmenus.includes(item.text) ? "rotate-90" : ""
                    }`}
                  />
                )}
              </Button>
              
              {item.subItems && openSubmenus.includes(item.text) && (
                <ul className="mt-1 ml-4 pl-3 border-l border-border/50 flex flex-col gap-1 py-1">
                  {item.subItems.map((subItem, subIndex) => (
                    <li key={subIndex}>
                      <Link to={subItem.link}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`h-auto w-full justify-start px-2 py-1.5 text-xs font-medium hover:bg-muted/50 transition-all duration-200 rounded-md ${
                            location.pathname.startsWith(subItem.link)
                              ? "bg-muted/80 text-primary font-medium"
                              : "text-muted-foreground"
                          }`}
                          onClick={() => setActiveMenuItem(item.text)}
                        >
                          <div className="flex items-center w-full gap-2">
                            <subItem.icon size={12} className="shrink-0 text-muted-foreground" />
                            <div className="flex flex-col items-start">
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-medium truncate">
                                  {subItem.text}
                                </span>
                                {subItem.beta && (
                                  <Badge className="px-1 py-0 h-4 bg-primary/10 text-primary text-[8px]">Beta</Badge>
                                )}
                              </div>
                              <span className="text-[10px] opacity-75 font-light truncate max-w-[160px]">
                                {subItem.description}
                              </span>
                            </div>
                          </div>
                        </Button>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </nav>
      
      <div className="mt-auto border-t border-border/40 py-4">
        <SwitchSellerAccount />
        
      </div>
    </aside>
  );
};
